import React, { useState, useEffect } from 'react';
import SEO from './SEO';
import { getBreadcrumbSchema, getFeedbackPageSchema, getFeedbackFormSchema, getOrganizationSchema } from '../utils/structuredData';
import './Feedback.css';

const Feedback = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    feedbackType: 'general',
    rating: 5,
    subject: '',
    message: '',
    browser: navigator.userAgent,
    url: window.location.href
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  // Math captcha state
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, answer: 0 });
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState('');

  // Generate new math captcha
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1; // 1-10
    const num2 = Math.floor(Math.random() * 10) + 1; // 1-10
    const answer = num1 + num2;
    setCaptcha({ num1, num2, answer });
    setCaptchaInput('');
    setCaptchaError('');
  };

  // Initialize captcha on component mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  // Breadcrumb data for feedback page
  const breadcrumbData = [
    { name: 'Home', url: '/' },
    { name: 'Feedback', url: '/#feedback' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setCaptchaError('');

    // Validate math captcha
    if (parseInt(captchaInput) !== captcha.answer) {
      setCaptchaError('Incorrect answer. Please try again.');
      setIsSubmitting(false);
      generateCaptcha(); // Generate new captcha
      return;
    }

    try {
      // Create formatted message content
      const messageContent = `
NEW FEEDBACK RECEIVED - Utility Tools

=== User Information ===
Name: ${formData.name}
Email: ${formData.email}
Feedback Type: ${formData.feedbackType.charAt(0).toUpperCase() + formData.feedbackType.slice(1)}
Rating: ${formData.rating}/5 stars

=== Feedback Details ===
Subject: ${formData.subject}

Message:
${formData.message}

=== Technical Information ===
URL: ${formData.url}
Browser: ${formData.browser}
Timestamp: ${new Date().toLocaleString()}

=== End of Feedback ===
      `.trim();

      // Prepare JSON data for Web3Forms (fixes CORS issue)
      const jsonData = {
        access_key: '0d24af32-2a98-47c6-bf5e-cfa6374695c3',
        name: formData.name,
        email: formData.email,
        subject: 'Utility Tools User Feedback',
        message: messageContent,
        feedback_type: formData.feedbackType,
        rating: formData.rating,
        user_subject: formData.subject,
        redirect: false
      };
      
      // Submit to Web3Forms with proper headers
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jsonData)
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        
        // Reset form after success
        setFormData({
          name: '',
          email: '',
          feedbackType: 'general',
          rating: 5,
          subject: '',
          message: '',
          browser: navigator.userAgent,
          url: window.location.href
        });
        generateCaptcha(); // Generate new captcha after successful submission
      } else {
        throw new Error(result.message || 'Submission failed');
      }

    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStarRating = () => {
    return (
      <div className="star-rating">
        <label className="rating-label">Rate your experience:</label>
        <div className="stars">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              className={`star ${star <= formData.rating ? 'filled' : ''}`}
              onClick={() => handleRatingChange(star)}
              aria-label={`Rate ${star} out of 5 stars`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24">
                <path
                  d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                  fill={star <= formData.rating ? "#ffc107" : "#e0e0e0"}
                />
              </svg>
            </button>
          ))}
        </div>
        <span className="rating-text">{formData.rating}/5 stars</span>
      </div>
    );
  };

  return (
    <div className="feedback-page">
      <SEO
        title="Feedback - Share Your Experience | Utility Tools"
        description="Help us improve Utility Tools by sharing your feedback, suggestions, and reporting any issues you encounter. Submit bug reports, feature requests, and general feedback through our secure form."
        keywords="feedback, user experience, suggestions, bug report, utility tools feedback, customer support, feature request, issue reporting, user satisfaction, product improvement"
        url="/#feedback"
        type="website"
        structuredData={[
          getBreadcrumbSchema(breadcrumbData),
          getFeedbackPageSchema(),
          getFeedbackFormSchema(),
          getOrganizationSchema()
        ]}
      />
      
      <div className="feedback-container">
        <div className="feedback-header">
          <button 
            className="btn back-btn-feedback"
            onClick={() => window.location.hash = 'home'}
          >
            ← Back to Home
          </button>
          <h1>Share Your Feedback</h1>
          <p className="feedback-description">
            Help us make Utility Tools better! Share your experience, suggestions, 
            or report any issues you've encountered.
          </p>
        </div>

        <div className="feedback-content">
          <div className="feedback-form-container">
            <form onSubmit={handleSubmit} className="feedback-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Your Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="feedbackType">Feedback Type</label>
                <select
                  id="feedbackType"
                  name="feedbackType"
                  value={formData.feedbackType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="general">General Feedback</option>
                  <option value="bug">Bug Report</option>
                  <option value="feature">Feature Request</option>
                  <option value="improvement">Improvement Suggestion</option>
                  <option value="performance">Performance Issue</option>
                  <option value="usability">Usability Issue</option>
                </select>
              </div>

              {renderStarRating()}

              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  placeholder="Brief description of your feedback"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Your Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="6"
                  placeholder="Please provide detailed feedback, steps to reproduce issues, or suggestions for improvement..."
                />
              </div>

              <div className="form-group captcha-group">
                <label htmlFor="captcha">Security Check</label>
                <div className="captcha-container">
                  <div className="captcha-question">
                    <span className="captcha-text">
                      What is {captcha.num1} + {captcha.num2}?
                    </span>
                    <button 
                      type="button" 
                      className="captcha-refresh" 
                      onClick={generateCaptcha}
                      aria-label="Generate new captcha"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24">
                        <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor"/>
                      </svg>
                    </button>
                  </div>
                  <input
                    type="number"
                    id="captcha"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    required
                    placeholder="Enter your answer"
                    className={captchaError ? 'error' : ''}
                  />
                  {captchaError && (
                    <div className="captcha-error">
                      <svg width="16" height="16" viewBox="0 0 20 20">
                        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" fill="none"/>
                        <path d="m8 8 4 4m0-4-4 4" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      {captchaError}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary btn-large"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="loading-spinner" width="20" height="20" viewBox="0 0 20 20">
                        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="31.416" strokeDashoffset="31.416">
                          <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
                          <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
                        </circle>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 20 20">
                        <path d="M2 3l16 7-16 7V3z" fill="currentColor"/>
                      </svg>
                      Send Feedback
                    </>
                  )}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="status-message success">
                  <svg width="20" height="20" viewBox="0 0 20 20">
                    <path d="M7 13l3 3 7-7" stroke="currentColor" strokeWidth="2" fill="none"/>
                  </svg>
                  Thank you! Your feedback has been submitted successfully. We appreciate your input!
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="status-message error">
                  <svg width="20" height="20" viewBox="0 0 20 20">
                    <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" fill="none"/>
                    <path d="m8 8 4 4m0-4-4 4" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  There was an error submitting your feedback. Please try again or email us directly.
                </div>
              )}
            </form>
          </div>

          <div className="feedback-info">
            <div className="info-card">
              <h3>Why Your Feedback Matters</h3>
              <ul>
                <li>Help us identify and fix bugs</li>
                <li>Guide feature development priorities</li>
                <li>Improve user experience design</li>
                <li>Enhance tool performance</li>
                <li>Expand utility tool offerings</li>
              </ul>
            </div>

            <div className="info-card">
              <h3>What Happens Next?</h3>
              <ol>
                <li>We review all feedback within 24-48 hours</li>
                <li>Bug reports are prioritized and tracked</li>
                <li>Feature requests are evaluated for roadmap</li>
                <li>You may receive follow-up questions</li>
                <li>Updates are implemented in future releases</li>
              </ol>
            </div>

            <div className="info-card">
              <h3>Alternative Contact</h3>
              <p>You can also reach us directly at:</p>
              <a href="mailto:info@navasakthienterprise.in" className="contact-email">
                info@navasakthienterprise.in
              </a>
            </div>
          </div>
        </div>
        
        {/* Feedback Page Footer */}
        <div className="feedback-footer">
          <div className="feedback-footer-content">
            <p className="feedback-copyright">
              © {new Date().getFullYear()} Utility Tools. All rights reserved.
            </p>
            <p className="feedback-powered-by">
              Powered by nkcodescribbler
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feedback;